import { Injectable, inject } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
// import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { map } from 'rxjs/operators';

import { Collection } from '../enums/collection.enum';
import { Entry } from '../models/entry';
import { addDoc, deleteDoc, doc, getDoc, getDocs, onSnapshot, serverTimestamp, setDoc,  } from '@angular/fire/firestore';
import { Firestore, collectionData, collection, where, query, orderBy, limit } from '@angular/fire/firestore';


@Injectable({
  providedIn: 'root'
})
export class EntryService {

  private firestore = inject(Firestore);
  private _auth = inject(Auth);
  private _entriesCollection: AngularFirestoreCollection<Entry>;
  private currentUser;
  private authSubscription;
  constructor(
    private _firestore: AngularFirestore,
    ) {
    this._entriesCollection = _firestore.collection<Entry>(Collection.Entries);
    this.authSubscription = onAuthStateChanged(this._auth, (user) => {
      if (user) {
        // User is signed in, see docs for a list of available properties
        // https://firebase.google.com/docs/reference/js/auth.user
        this.currentUser = user;
        // ...
      } else {
        // User is signed out
        this.currentUser = null;
      }
    });
  }
  ngOnDestroy(): void {
    if(this.authSubscription) {
      // unsubscribes
      this.authSubscription();
    }
  }
  list(){
    return this._entriesCollection.valueChanges({'idField':'id'}).pipe(
      map(x => x.map(y => Object.assign(new Entry, y)))
    );
  }
  async get(id: string){
    const docRef = doc(this.firestore, Collection.Entries, id);
    return await getDoc(docRef);
  }
  getByEvent(eventId: string){
    const q = query(
      collection(this.firestore, Collection.Entries),
      where('eventId', '==', eventId),
      orderBy('lane'),
    );
    return collectionData(q).pipe(
      map(x => x.map(y => Object.assign(new Entry, y)))
    );
  }
  async add(entry: Entry) {
    entry.owner = this.currentUser.uid;
    entry.created = serverTimestamp();
    const newEntryRef = doc(collection(this.firestore, Collection.Entries));
    entry.id = newEntryRef.id
    setDoc(newEntryRef, entry)
    return entry.id;
  }
  update(entry: Entry) {
    entry.modified = serverTimestamp();
    this._entriesCollection.doc(entry.id).update(Object.assign({}, entry));
  }
  async startEntries(eventId: string, entryIds: string[]){
    // Get a new write batch
    const batch = this._firestore.firestore.batch();

    // Create start value
    const start = serverTimestamp();

    // Set start for event
    const eventRef = this._firestore.firestore.collection(Collection.Events).doc(eventId);
    batch.update(eventRef, {officialTime: start});

    // Set start for each entry
    entryIds.forEach( id => {
      const entryRef = this._firestore.firestore.collection(Collection.Entries).doc(id);
      batch.update(entryRef, {start: start});
    })

    // Commit the batch
    await batch.commit();
  }
  async resetEntries(eventId: string, entryIds: string[]){
    // Get a new write batch
    const batch = this._firestore.firestore.batch();

    // Set start for event
    const eventRef = this._firestore.firestore.collection(Collection.Events).doc(eventId);
    batch.update(eventRef, {officialTime: null});

    // Set start for each entry to null
    entryIds.forEach( id => {
      const entryRef = this._firestore.firestore.collection(Collection.Entries).doc(id);
      batch.update(entryRef, {start: null, finish: null});
    })

    // Commit the batch
    await batch.commit();
  }
  async finishEntries(entryIds: string[]){
    // Get a new write batch
    const batch = this._firestore.firestore.batch();

    // Create finish value
    const finish = serverTimestamp();

    // Set finish for each entry
    entryIds.forEach( id => {
      const entryRef = this._firestore.firestore.collection(Collection.Entries).doc(id);
      batch.update(entryRef, {finish: finish});
    })

    // Commit the batch
    await batch.commit();
  }
  async updateReferees(referees: string[], eventIds: string[]){
    // Get a new write batch
    const batch = this._firestore.firestore.batch();
    // "where in" filters have a max of ten provided values
    while (eventIds.length) {
      const ids = eventIds.splice(0, 10);
      // Get all entries
      const snapshot = await this._firestore.firestore.collection(Collection.Entries)
        .where('eventId', 'in', ids).get();
      // add results to batch
      snapshot.forEach((doc) => {
        const entryRef = this._firestore.firestore.collection(Collection.Entries).doc(doc.id);
        batch.update(entryRef, {referees: referees});
      })
    }  // loop until none remain

    // Commit the batch
    await batch.commit();
  }
  async deleteByEvents(eventIds: string[]){
    // Get a new delete batch
    const batch = this._firestore.firestore.batch();
    // "where in" filters have a max of ten provided values
    while (eventIds.length) {
      const ids = eventIds.splice(0, 10);
      // Get all entries
      const snapshot = await this._firestore.firestore.collection(Collection.Entries)
        .where('eventId', 'in', ids).get();
      // add results to batch
      snapshot.forEach((doc) => {
        const entryRef = this._firestore.firestore.collection(Collection.Entries).doc(doc.id);
        batch.delete(entryRef);
      })
    }  // loop until none remain

    // Commit the batch
    await batch.commit();
  }
}
